import type { Coordinate } from '@shared/models/geography.model';

/** Map operations a location search needs in order to move and restore the origin. */
export type LocationSearchMap = {
  getLocation: () => Coordinate;
  flyToLocation: (location: Coordinate) => void;
};

type ApplyLocationSearchSelectionInput = {
  map: LocationSearchMap;
  coordinate: Coordinate;
  select: (coordinate: Coordinate) => void | Promise<unknown>;
  /** False when a newer search has already replaced this one. */
  isCurrent: () => boolean;
};

/**
 * Fly the map to a searched {@link Coordinate} and save it.
 * When the save rejects, fly back to the origin the map showed before this
 * search. When `isCurrent` is false, a newer search already owns the origin,
 * so this failure leaves that origin in place.
 */
export const applyLocationSearchSelection = async ({
  map,
  coordinate,
  select,
  isCurrent,
}: ApplyLocationSearchSelectionInput): Promise<void> => {
  // Snapshot. A map may return its live coordinate and mutate that object on fly.
  const shown = map.getLocation();
  const previous = { lat: shown.lat, lng: shown.lng };
  map.flyToLocation(coordinate);
  try {
    await select(coordinate);
  } catch (error) {
    if (!isCurrent()) return;
    console.warn('Failed to update building location', error);
    map.flyToLocation(previous);
  }
};
